import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { LogIn, Mail, Lock, AlertCircle, CheckCircle2, Package, Calendar, KeyRound, Send } from "lucide-react";
import { PLANES, FRECUENCIAS_PAGO } from "@/pages/Landing";

export default function AuthPage({ mode = "login" }) {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  // Plan pre-seleccionado desde la URL (?plan=oro) o fallback al Plan Plata
  const initialPlan = (() => {
    const p = (searchParams.get("plan") || "").toLowerCase();
    return PLANES.find(x => x.key === p) ? p : "plata";
  })();
  const [plan, setPlan] = useState(initialPlan);
  const [frecuencia, setFrecuencia] = useState("mensual");
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingInfo, setPendingInfo] = useState(null); // { email }
  // Multi-tenant login: cuando el email tiene perfiles en varias empresas,
  // mostramos un selector después de validar la contraseña.
  const [accounts, setAccounts] = useState(null); // Array | null

  // Estado del OTP (solo aplica en modo register)
  const [step, setStep] = useState("email");        // "email" → "code"
  const [code, setCode] = useState("");
  const [sendingCode, setSendingCode] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setInterval(() => setResendCooldown(s => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  const requestCode = async () => {
    setErr(""); setInfo("");
    if (!email) { setErr("Ingresá tu correo primero"); return; }
    setSendingCode(true);
    try {
      const { data } = await api.post("/auth/request-verification-code", { email });
      setInfo(data?.message || `Enviamos un código a ${email}.`);
      setStep("code");
      setResendCooldown(60);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setSendingCode(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setInfo("");
    if (mode === "register") {
      if (step === "email") {
        await requestCode();
        return;
      }
      if (!code || code.length < 6) {
        setErr("Ingresá el código de 6 dígitos");
        return;
      }
    }
    setLoading(true);
    try {
      if (mode === "login") {
        // Paso 1: preguntamos al backend qué cuentas activas tiene este email
        // (multi-tenant). Si solo hay una, hacemos login directo. Si hay más,
        // mostramos un selector antes de completar el login.
        const { data } = await api.post("/auth/accounts", { email, password });
        const active = (data?.accounts || []).filter(a => a.estado === "activo");
        if (active.length === 0) {
          // Caer al login normal: dejará que el backend devuelva el 403 con el
          // mensaje adecuado (pendiente / rechazado / suspendido).
          await login(email, password);
          nav("/dashboard");
        } else if (active.length === 1) {
          await login(email, password, active[0].empresa_id);
          nav("/dashboard");
        } else {
          // Múltiples cuentas activas → mostrar selector
          setAccounts(active);
        }
      } else {
        await register({ email, password, plan, frecuencia_pago: frecuencia, verification_code: code });
        setPendingInfo({ email });
      }
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  const confirmarEmpresa = async (empresa_id) => {
    setErr(""); setLoading(true);
    try {
      await login(email, password, empresa_id);
      nav("/dashboard");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex items-stretch bg-app">
      {/* Left brand panel */}
      <div className="hidden lg:flex w-1/2 bg-sidebar text-white p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "radial-gradient(circle at 30% 20%, #D96C4A 0%, transparent 50%), radial-gradient(circle at 70% 80%, #3A6B50 0%, transparent 60%)" }} />
        <div className="relative">
          <div className="flex items-center gap-3 mb-12">
            <img src="/vertice-logo.png" alt="Vértice" className="h-12 w-auto brightness-0 invert" />
            <div>
              <div className="text-xs uppercase tracking-widest opacity-60">Sistema de Recursos Humanos</div>
            </div>
          </div>
          <h1 className="font-display text-5xl font-semibold leading-[1.1] mb-6">Planillas que se calculan solas.</h1>
          <p className="text-base text-sidebar opacity-80 max-w-md leading-relaxed">
            Gestión integral de personal, asistencia y nómina para empresas costarricenses. Cálculos automáticos con CCSS, horas extra y rebajos.
          </p>
        </div>
        <div className="relative space-y-3">
          {["Control de asistencia diario","Cálculo de planillas semanales / mensuales","Deducciones CCSS automáticas (10.83%)","Gestión integral de empleados"].map((f) => (
            <div key={f} className="flex items-center gap-3 text-sm opacity-80">
              <div className="w-1.5 h-1.5 rounded-full bg-accent-c" /> {f}
            </div>
          ))}
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md fade-in-up">
          {accounts ? (
            <div data-testid="empresa-selector-screen">
              <div className="mb-6">
                <div className="lg:hidden flex items-center gap-2 mb-6">
                  <img src="/vertice-logo.png" alt="Vértice" className="h-10 w-auto" />
                </div>
                <h2 className="font-display text-3xl font-semibold mb-1.5">Elegí una cuenta</h2>
                <p className="text-sm text-secondary-c">
                  Tu correo <strong className="text-primary-c">{email}</strong> tiene acceso a varias empresas. Seleccioná con cuál querés ingresar.
                </p>
              </div>
              <div className="space-y-2 mb-4">
                {accounts.map(a => (
                  <button
                    key={a.user_id}
                    type="button"
                    onClick={() => confirmarEmpresa(a.empresa_id)}
                    disabled={loading}
                    data-testid={`empresa-option-${a.empresa_id}`}
                    className="w-full text-left p-4 rounded-xl border border-c hover:border-primary-c hover:bg-[#FAFAF7] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-primary-c truncate">{a.empresa_nombre}</div>
                      <div className="text-xs text-secondary-c mt-0.5 capitalize">
                        {a.role === "super_admin" ? "Super administrador" : a.role === "admin" ? "Administrador" : "Asistente"}
                        {a.is_owner ? " · Titular" : ""}
                      </div>
                    </div>
                    <LogIn size={18} className="text-secondary-c flex-shrink-0" />
                  </button>
                ))}
              </div>
              {err && (
                <div className="p-3 rounded-xl bg-[#FEEBE8] border border-[#F5C6CB] text-sm text-error flex items-start gap-2 mb-3">
                  <AlertCircle size={16} className="mt-0.5 flex-shrink-0" /> {err}
                </div>
              )}
              <button
                type="button"
                onClick={() => { setAccounts(null); setErr(""); }}
                className="text-sm text-secondary-c hover:text-primary-c underline underline-offset-4"
                data-testid="empresa-selector-back"
              >
                ← Volver
              </button>
            </div>
          ) : pendingInfo ? (
            <div data-testid="pending-approval-screen" className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#E6EFEA] flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 size={32} className="text-primary-color" />
              </div>
              <h2 className="font-display text-3xl font-semibold mb-3">¡Cuenta creada!</h2>
              <p className="text-secondary-c mb-6 leading-relaxed">
                Tu cuenta <strong className="text-primary-c">{pendingInfo.email}</strong> está <strong>pendiente de aprobación</strong>.
                Te enviaremos un correo tan pronto sea aprobada.
              </p>
              <div className="p-4 rounded-xl bg-[#FFF8E1] border border-[#F0DDA0] text-sm text-primary-c mb-6 text-left">
                <strong>Mientras tanto:</strong> por seguridad, la aprobación es manual. Nuestro equipo la activará lo antes posible (normalmente en menos de 24h en días hábiles). Al iniciar sesión podrás completar los datos de tu empresa.
              </div>
              <Link to="/login" className="np-btn np-btn-primary w-full justify-center" data-testid="goto-login-from-pending">
                <LogIn size={16} /> Volver a Iniciar sesión
              </Link>
            </div>
          ) : (
          <>
          <div className="mb-8">
            <div className="lg:hidden flex items-center gap-2 mb-6">
              <img src="/vertice-logo.png" alt="Vértice" className="h-10 w-auto" />
            </div>
            <h2 className="font-display text-3xl font-semibold mb-1.5">{mode === "login" ? "Bienvenido de vuelta" : "Creá tu cuenta"}</h2>
            <p className="text-sm text-secondary-c">{mode === "login" ? "Iniciá sesión en tu cuenta" : "Empezá a gestionar tu planilla en minutos"}</p>
          </div>

          <button onClick={googleLogin} className="np-btn np-btn-outline w-full mb-4" data-testid="google-login-btn">
            <svg width="18" height="18" viewBox="0 0 18 18"><path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.71v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.61z"/><path fill="#34A853" d="M9 18c2.43 0 4.47-.81 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18z"/><path fill="#FBBC05" d="M3.97 10.71A5.41 5.41 0 0 1 3.68 9c0-.6.1-1.17.29-1.71V4.96H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.04l3.01-2.33z"/><path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.59C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.96L3.97 7.3C4.68 5.16 6.66 3.58 9 3.58z"/></svg>
            {mode === "login" ? "Continuar con Google" : "Registrarse con Google (solo cuentas existentes)"}
          </button>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-[var(--border)]" />
            <span className="text-xs uppercase tracking-widest text-secondary-c">o con email</span>
            <div className="flex-1 h-px bg-[var(--border)]" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="np-label">Correo electrónico</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary-c" />
                <input data-testid="email-input" type="email" className="np-input pl-9" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="tu@empresa.cr" />
              </div>
            </div>
            <div>
              <label className="np-label">Contraseña</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary-c" />
                <input data-testid="password-input" type="password" className="np-input pl-9" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" />
              </div>
              {mode === "login" && (
                <div className="text-right mt-1.5">
                  <Link to="/forgot-password" className="text-xs text-accent-color font-semibold hover:underline" data-testid="goto-forgot-password">
                    ¿Olvidaste tu contraseña?
                  </Link>
                </div>
              )}
            </div>

            {mode === "register" && (
              <>
                <div>
                  <label className="np-label"><Package size={12} className="inline mr-1" /> Plan</label>
                  <select className="np-input" value={plan} onChange={e => setPlan(e.target.value)} data-testid="register-plan" disabled={step === "code"}>
                    {PLANES.map(p => (
                      <option key={p.key} value={p.key}>
                        Plan {p.nombre} — ₡{p.base.toLocaleString("es-CR")} base + ₡{p.porColab.toLocaleString("es-CR")}/colaborador
                      </option>
                    ))}
                  </select>
                  <p className="text-[11px] text-secondary-c mt-1">Todos incluyen IVA. Podés cambiar de plan más adelante.</p>
                </div>
                <div>
                  <label className="np-label"><Calendar size={12} className="inline mr-1" /> Frecuencia de pago</label>
                  <select className="np-input" value={frecuencia} onChange={e => setFrecuencia(e.target.value)} data-testid="register-frecuencia" disabled={step === "code"}>
                    {FRECUENCIAS_PAGO.map(f => (
                      <option key={f.key} value={f.key}>
                        {f.label}{f.descuento > 0 ? ` — ${Math.round(f.descuento * 100)}% descuento` : ""}
                      </option>
                    ))}
                  </select>
                  <p className="text-[11px] text-secondary-c mt-1">Los ciclos más largos dan mejor descuento. Primer cobro después de 25 días de prueba.</p>
                </div>

                {step === "code" && (
                  <div>
                    <label className="np-label"><KeyRound size={12} className="inline mr-1" /> Código de verificación</label>
                    <div className="relative">
                      <KeyRound size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary-c" />
                      <input
                        data-testid="verification-code-input"
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={6}
                        className="np-input pl-9 tracking-widest text-lg text-center font-mono"
                        value={code}
                        onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        required
                        placeholder="123456"
                      />
                    </div>
                    <p className="text-[11px] text-secondary-c mt-1">
                      Revisá tu correo. El código vence en 10 minutos.{" "}
                      {resendCooldown > 0 ? (
                        <span className="text-secondary-c">Podés reenviar en {resendCooldown}s</span>
                      ) : (
                        <button type="button" onClick={requestCode} disabled={sendingCode} className="text-accent-color font-semibold hover:underline" data-testid="resend-code-btn">
                          {sendingCode ? "Enviando..." : "Reenviar código"}
                        </button>
                      )}
                    </p>
                  </div>
                )}
              </>
            )}

            {info && <div data-testid="auth-info" className="flex items-start gap-2 p-3 rounded-xl bg-[#E6EFEA] text-primary-c text-sm"><CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" /> {info}</div>}
            {err && <div data-testid="auth-error" className="flex items-center gap-2 p-3 rounded-xl bg-[#FDEDED] text-[#B84B4B] text-sm"><AlertCircle size={16} /> {err}</div>}

            <button type="submit" disabled={loading || sendingCode} className="np-btn np-btn-primary w-full" data-testid="auth-submit-btn">
              {loading || sendingCode ? "Cargando..." : (
                mode === "login" ? (<><LogIn size={16} /> Iniciar sesión</>)
                : step === "email" ? (<><Send size={16} /> Enviar código de verificación</>)
                : (<><LogIn size={16} /> Crear cuenta</>)
              )}
            </button>
          </form>

          <div className="text-center text-sm mt-6 text-secondary-c">
            {mode === "login" ? (
              <>¿No tenés cuenta? <Link to="/register" className="text-accent-color font-semibold hover:underline" data-testid="goto-register">Crear una</Link></>
            ) : (
              <>¿Ya tenés cuenta? <Link to="/login" className="text-accent-color font-semibold hover:underline" data-testid="goto-login">Iniciar sesión</Link></>
            )}
          </div>
          </>
          )}
        </div>
      </div>
    </div>
  );
}
